import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {PayloadStates} from '@lore/utils';
import {
  Accordion,
  LinkButton,
  ListSelect,
  Loader,
  ProjectSharingSuggestions,
  SharePanel,
  SocialShareButtons
} from '@communityshare/ui-shared';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import useDialogLauncher from '@/dialogs-routable/_hooks/useDialogLauncher';
import {DialogManager} from '@/dialogs_new/DialogManager';
import useActiveProjects from '@/pages/my-leading/useActiveProjects';
import ProjectLinkPreview from './ProjectLinkPreview';

// Brand name is not translatable, so it is interpolated into the strings.
const BRAND = 'CommunityShare';

ProjectShareDialog.propTypes = {
  // When omitted, the user picks from their own active projects.
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string,
    data: PropTypes.shape({
      title: PropTypes.string
    })
  }),
  onCancel: PropTypes.func.isRequired
};

export default function ProjectShareDialog(props) {
  const { project: fixedProject, onCancel } = props;
  const { t } = useTranslation();
  const launch = useDialogLauncher();
  const isProjectFixed = Boolean(fixedProject);

  const activeProjects = useActiveProjects();
  const [selectedProjectId, setSelectedProjectId] = useState(fixedProject?.id ?? null);

  // Default the selection to the first active project once the list loads.
  useEffect(() => {
    if (!isProjectFixed && !selectedProjectId && activeProjects.data?.length > 0) {
      setSelectedProjectId(activeProjects.data[0].id);
    }
  }, [isProjectFixed, selectedProjectId, activeProjects.data]);

  if (isProjectFixed && fixedProject.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader />
      </DialogTemplate>
    );
  }

  if (!isProjectFixed && activeProjects.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader />
      </DialogTemplate>
    );
  }

  const project = isProjectFixed ?
    fixedProject :
    (activeProjects.data || []).find((activeProject) => activeProject.id === selectedProjectId);

  if (!project) {
    return (
      <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
        <CloseDialogButton onCancel={onCancel} />
        <div className="px-6 pt-6 pb-8 sm:px-8">
          <div className="mt-5">
            <LinkButton
              label={t('projects.share.back_to_home')}
              icon="left"
              onClick={onCancel}
            />
          </div>
          <p className="mt-6 text-base text-cs-gray-500">
            {t('projects.share.no_active_projects')}
          </p>
        </div>
      </DialogTemplate>
    );
  }

  const shareUrl = `${window.location.origin}/share/project/${project.id}`;

  function onEmail() {
    window.location.href = `mailto:?subject=${encodeURIComponent(project.data.title)}&body=${encodeURIComponent(shareUrl)}`;
  }

  function onLinkedIn() {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onFacebook() {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onCopyLink() {
    navigator.clipboard.writeText(shareUrl);
  }

  return (
    <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
      <CloseDialogButton onCancel={onCancel} />
      <SharePanel
        breadcrumbParent={t('projects.share.breadcrumb_parent')}
        breadcrumbAction={t('projects.share.breadcrumb_action')}
        backLabel={isProjectFixed ? t('projects.share.back_to_project') : t('projects.share.back_to_home')}
        onBack={onCancel}
        title={t('projects.share.title')}
        subtitle={t('projects.share.subtitle')}
        selectLabel={t('projects.share.field_label')}
        select={(
          <ListSelect
            value={project}
            options={activeProjects.data || []}
            disabled={isProjectFixed}
            getOptionLabel={(activeProject) => activeProject.data.title}
            getOptionKey={(activeProject) => activeProject.id}
            onChange={(activeProject) => setSelectedProjectId(activeProject.id)}
            className="bg-white"
          />
        )}
        calloutBold={t('projects.share.callout_bold')}
        calloutBody={t('projects.share.callout_body')}
        calloutLinkLabel={t('projects.share.edit_guiding_question')}
        onCalloutLinkClick={() => launch(DialogManager.ProjectTitleEdit.Config(project.id))}
        suggestions={(
          <Accordion>
            <ProjectSharingSuggestions
              options={[
                {
                  key: 'find_partners',
                  label: t('projects.share.option_find_partners'),
                  template: t('projects.share.template_find_partners', {guidingQuestion: project.data.title || 'INCOMPLETE'})
                },
                {
                  key: 'spread_word',
                  label: t('projects.share.option_spread_word', { brand: BRAND }),
                  template: t('projects.share.template_spread_word', { brand: BRAND })
                }
              ]}
              examples={[
                { id: 'example-1', text: t('projects.share.example_1') },
                { id: 'example-2', text: t('projects.share.example_2') },
                { id: 'example-3', text: t('projects.share.example_3', { brand: BRAND }) }
              ]}
              helpTitle={t('projects.share.help_title')}
              viewSuggestionsLabel={t('projects.share.view_suggestions')}
              helpQuestion={t('projects.share.help_question')}
              templateLabel={t('projects.share.template_label')}
              resetTemplateLabel={t('projects.share.reset_template')}
              copyLabel={t('projects.share.copy_text')}
              copiedLabel={t('projects.share.copied')}
              seeExamplesLabel={t('projects.share.see_examples')}
            />
          </Accordion>
        )}
        shareActions={(
          <SocialShareButtons
            onEmail={onEmail}
            onLinkedIn={onLinkedIn}
            onFacebook={onFacebook}
            onCopyLink={onCopyLink}
            copiedLinkLabel={t('controls.Copied')}
            copyLinkLabel={t('controls.Copy link')}
          />
        )}
      >
        {/* key remounts the preview on project change so project-specific
            state (selected image, default-save guard) resets and the
            preview stays in sync with the selected project. */}
        <ProjectLinkPreview key={project.id} project={project} />
      </SharePanel>
    </DialogTemplate>
  );
}
